<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div id="content" class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Header Section -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="payroll-header">
                <div class="payroll-header-left">
                  <div class="payroll-badge">PAYROLL</div>
                  <h1 class="payroll-title">Payroll Hub</h1>
                  <p class="payroll-description">Process payroll, manage runs, and generate compliance reports.</p>
                </div>
                <button type="button" class="btn-config" @click="$router.push('/payroll-setup')">
                  <i class="bx bx-slider-alt"></i>
                  Configure Payroll
                </button>
              </div>
            </div>
          </div>

          <!-- Stats Cards -->
          <div class="row g-3 mb-4">
            <div class="col-md-3 col-sm-6">
              <div class="stat-card">
                <div class="stat-card-icon blue">
                  <i class="bx bx-receipt"></i>
                </div>
                <div class="stat-card-info">
                  <span class="stat-card-value">{{ runs.length }}</span>
                  <span class="stat-card-label">Runs Loaded</span>
                </div>
              </div>
            </div>
            <div class="col-md-3 col-sm-6">
              <div class="stat-card">
                <div class="stat-card-icon purple">
                  <i class="bx bx-hash"></i>
                </div>
                <div class="stat-card-info">
                  <span class="stat-card-value">{{ lastRunId || '—' }}</span>
                  <span class="stat-card-label">Selected Run ID</span>
                </div>
              </div>
            </div>
            <div class="col-md-3 col-sm-6">
              <div class="stat-card">
                <div class="stat-card-icon green">
                  <i class="bx bx-user-check"></i>
                </div>
                <div class="stat-card-info">
                  <span class="stat-card-value">{{ items.length }}</span>
                  <span class="stat-card-label">Lines Loaded</span>
                </div>
              </div>
            </div>
            <div class="col-md-3 col-sm-6">
              <div class="stat-card">
                <div class="stat-card-icon orange">
                  <i class="bx bx-wallet"></i>
                </div>
                <div class="stat-card-info">
                  <span class="stat-card-value">{{ formatMoney(itemsNetTotal) }}</span>
                  <span class="stat-card-label">Net Total</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Main Card -->
          <div class="payroll-card">
            <div class="payroll-card-header">
              <h5 class="payroll-card-title">Monthly Payroll</h5>
            </div>
            <div class="payroll-card-body">
              <!-- Filters Row -->
              <div class="filters-row">
                <div class="filter-group">
                  <label class="filter-label">Payroll Group</label>
                  <select v-model.number="selectedConfigId" class="filter-select">
                    <option :value="0" disabled>Select payroll group</option>
                    <option v-for="cfg in payrollConfigs" :key="cfg.id" :value="Number(cfg.id)">
                      {{ cfg.config_name }}
                    </option>
                  </select>
                </div>
                <div class="filter-group">
                  <label class="filter-label">Month</label>
                  <input v-model.number="runMonth" type="number" min="1" max="12" class="filter-input" />
                </div>
                <div class="filter-group">
                  <label class="filter-label">Year</label>
                  <input v-model.number="runYear" type="number" min="2000" class="filter-input" />
                </div>
                <div class="filter-group">
                  <label class="filter-label">Payroll Mode</label>
                  <select v-model="payrollMode" class="filter-select">
                    <option value="" disabled>Select mode</option>
                    <option value="monthly">Monthly</option>
                    <option value="commission">Commission</option>
                  </select>
                </div>
                <div class="filter-group">
                  <label class="filter-label">Run ID</label>
                  <input v-model.number="lastRunId" type="number" class="filter-input" placeholder="From runs list" />
                </div>
              </div>

              <!-- Info Badge -->
              <div class="info-badge">
                <i class="bx bx-info-circle"></i>
                <span>Group: <strong>{{ selectedConfigName }}</strong> — Using <strong>{{ payrollMode }}</strong> mode with effective date <strong>{{ periodEndLabel }}</strong></span>
              </div>

              <!-- Tabs -->
              <div class="payroll-tabs">
                <button class="tab-btn" :class="{ active: activeTab === 'runs' }" @click="activeTab = 'runs'">
                  <i class="bx bx-list-ul"></i>
                  Runs & Actions
                </button>
                <button class="tab-btn" :class="{ active: activeTab === 'master' }" @click="activeTab = 'master'">
                  <i class="bx bx-grid-alt"></i>
                  Master Payroll
                </button>
              </div>

              <!-- Message Alert -->
              <div v-if="message" class="message-alert">
                <i class="bx bx-bell"></i>
                <span>{{ message }}</span>
              </div>

              <!-- Runs Tab -->
              <div v-show="activeTab === 'runs'" class="tab-content">
                <div class="action-buttons">
                  <button class="btn-primary-sm" :disabled="loading" @click="generateRun">
                    <i class="bx bx-plus-circle"></i> Generate Payroll
                  </button>
                  <button class="btn-outline-sm" :disabled="loading" @click="loadRuns">
                    <i class="bx bx-refresh"></i> Refresh
                  </button>
                  <button class="btn-success-sm" :disabled="loading" @click="approveRun">
                    <i class="bx bx-check-circle"></i> Approve
                  </button>
                  <button class="btn-dark-sm" :disabled="loading" @click="markPaid">
                    <i class="bx bx-credit-card"></i> Mark Paid
                  </button>
                </div>

                <!-- Compliance Exports -->
                <div class="compliance-section">
                  <div class="compliance-header">
                    <i class="bx bx-file"></i>
                    <span>Compliance Exports (Selected Run)</span>
                  </div>
                  <div class="compliance-buttons">
                    <button class="compliance-btn" @click="prepareComplianceExport('KRA')">KRA (PAYE)</button>
                    <button class="compliance-btn" @click="prepareComplianceExport('NSSF')">NSSF</button>
                    <button class="compliance-btn" @click="prepareComplianceExport('SHA')">SHA/SHIF</button>
                    <button class="compliance-btn" @click="prepareComplianceExport('HOUSING')">Housing Levy</button>
                    <div class="p9-group">
                      <input v-model.number="p9Year" type="number" placeholder="Year" class="p9-input" />
                      <button class="compliance-btn green" @click="prepareP9Export">Export P9</button>
                    </div>
                  </div>
                </div>

                <!-- Runs Table -->
                <div class="section-title-sm">
                  <i class="bx bx-table"></i>
                  <span>Payroll Runs — Click a row to select</span>
                </div>
                <div class="table-container" v-if="runs.length">
                  <table class="data-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Period</th>
                        <th>Status</th>
                        <th>Net Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="r in runs"
                        :key="r.id"
                        :class="{ selected: Number(r.id) === Number(lastRunId) }"
                        @click="lastRunId = Number(r.id)"
                      >
                        <td class="id-cell">#{{ r.id }}</td>
                        <td>{{ r.month }}/{{ r.year }} — {{ r.config_name }}</td>
                        <td><span class="status-badge" :class="r.status">{{ r.status }}</span></td>
                        <td class="amount-cell">{{ formatMoney(r.total_net) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="empty-state">
                  <i class="bx bx-folder-open"></i>
                  <p>No payroll runs yet. Generate your first run above.</p>
                </div>
              </div>

              <!-- Master Tab -->
              <div v-show="activeTab === 'master'" class="tab-content">
                <div v-if="selectedRun" class="run-info">
                  <i class="bx bx-calendar"></i>
                  <span>Run #{{ selectedRun.id }} — {{ selectedRun.month }}/{{ selectedRun.year }} ({{ selectedRun.status }}) — Net total: {{ formatMoney(selectedRun.total_net) }}</span>
                </div>
                <div v-else class="warning-badge">
                  <i class="bx bx-error-circle"></i>
                  <span>No run selected. Generate or click a run from Runs tab.</span>
                </div>

                <div class="action-buttons" v-if="lastRunId">
                  <button class="btn-outline-sm" :disabled="loading" @click="loadItems">
                    <i class="bx bx-refresh"></i> Refresh Register
                  </button>
                  <button class="btn-primary-sm" :disabled="loading" @click="prepareBankPaymentExport">
                    <i class="bx bx-download"></i> Bank Payment
                  </button>
                  <button class="btn-outline-sm" :disabled="loading" @click="prepareMasterRegisterExport">
                    <i class="bx bx-export"></i> Full Register
                  </button>
                  <button class="btn-email-sm" :disabled="loading" @click="sendAllPayslipsEmail">
                    <i class="bx bx-envelope"></i> Send All Payslips
                  </button>
                </div>

                <!-- Register Stats -->
                <div class="stats-mini-row" v-if="items.length">
                  <div class="stat-mini">
                    <span class="stat-mini-label">Gross</span>
                    <span class="stat-mini-value">{{ formatMoney(registerTotals.gross) }}</span>
                  </div>
                  <div class="stat-mini">
                    <span class="stat-mini-label">Deductions</span>
                    <span class="stat-mini-value">{{ formatMoney(registerTotals.deductions) }}</span>
                  </div>
                  <div class="stat-mini">
                    <span class="stat-mini-label">Net</span>
                    <span class="stat-mini-value">{{ formatMoney(registerTotals.net) }}</span>
                  </div>
                  <div class="stat-mini">
                    <span class="stat-mini-label">Employees</span>
                    <span class="stat-mini-value">{{ filteredRegisterRows.length }}</span>
                  </div>
                </div>

                <!-- Search -->
                <div class="search-row" v-if="items.length">
                  <i class="bx bx-search"></i>
                  <input v-model.trim="registerSearch" type="text" placeholder="Search employee, KRA PIN, bank..." class="search-input" />
                </div>

                <!-- Master Table -->
                <div class="table-container master-table" v-if="items.length">
                  <table class="data-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Bank</th>
                        <th>Account</th>
                        <th>Basic</th>
                        <th>Gross</th>
                        <th>PAYE</th>
                        <th>NSSF</th>
                        <th>SHA</th>
                        <th>Housing</th>
                        <th>Net</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="it in filteredRegisterRows" :key="it.id">
                        <td class="employee-cell">
                          <div class="employee-avatar">
                            {{ (it.firstName?.[0] || '') + (it.lastName?.[0] || '') }}
                          </div>
                          <div class="employee-info">
                            <span class="employee-name">{{ it.firstName }} {{ it.lastName }}</span>
                            <span class="employee-id">{{ it.empNo }}</span>
                          </div>
                        </td>
                        <td>{{ it.bank_name || '—' }}</td>
                        <td>{{ it.account_no || '—' }}</td>
                        <td class="amount-cell">{{ formatMoney(it.basic_salary) }}</td>
                        <td class="amount-cell">{{ formatMoney(it.gross_pay) }}</td>
                        <td class="amount-cell">{{ formatMoney(it.paye) }}</td>
                        <td class="amount-cell">{{ formatMoney(it.nssf) }}</td>
                        <td class="amount-cell">{{ formatMoney(it.shif) }}</td>
                        <td class="amount-cell">{{ formatMoney(it.housing_levy) }}</td>
                        <td class="amount-cell net">{{ formatMoney(it.net_pay) }}</td>
                        <td class="action-cell">
                          <button class="icon-btn" @click="openPayslip(it.raw)" title="View Payslip">
                            <i class="bx bx-receipt"></i>
                          </button>
                          <button class="icon-btn email" @click="sendPayslipEmail(it.raw)" title="Email Payslip">
                            <i class="bx bx-envelope"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else-if="lastRunId && !items.length" class="empty-state">
                  <i class="bx bx-spinner"></i>
                  <p>Click "Refresh Register" to load payroll lines.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Export Modal -->
    <div v-if="exportPreview" class="modal-overlay" @click.self="closeExportPreview">
      <div class="modal-container export-modal">
        <div class="modal-header">
          <h5>{{ exportPreview.title }}</h5>
          <button class="modal-close" @click="closeExportPreview">×</button>
        </div>
        <div class="modal-body">
          <div class="preview-table-wrapper">
            <table class="preview-table">
              <thead>
                <tr>
                  <th v-for="h in exportPreview.headers" :key="h">{{ h }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in exportPreviewTableRows" :key="idx">
                  <td v-for="(cell, ci) in row" :key="ci">{{ cell }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline-sm" @click="closeExportPreview">Cancel</button>
          <button class="btn-primary-sm" @click="confirmExportDownload">Download CSV</button>
        </div>
      </div>
    </div>

    <!-- Payslip Modal -->
    <div v-if="selectedPayslip" class="modal-overlay" @click.self="selectedPayslip = null">
      <div class="modal-container payslip-modal">
        <div class="modal-header">
          <h5>Employee Payslip</h5>
          <div class="modal-actions">
            <button class="icon-btn" @click="sendPayslipEmail(selectedPayslip)" title="Send Email">
              <i class="bx bx-envelope"></i>
            </button>
            <button class="modal-close" @click="selectedPayslip = null">×</button>
          </div>
        </div>
        <div class="modal-body">
          <div class="payslip">
            <div class="payslip-header">
              <div class="payslip-company">
                <img v-if="companyLogoUrl" :src="companyLogoUrl" alt="Logo" class="payslip-logo" />
                <div>
                  <h4>{{ companyDisplayName }}</h4>
                  <p class="company-address">{{ companyInfo.companyAddress || 'Company address not set' }}</p>
                </div>
              </div>
              <div class="payslip-meta">
                <span class="payslip-badge">PAYSLIP</span>
                <span class="payslip-period">{{ payslipPeriodText }}</span>
              </div>
            </div>

            <div class="payslip-details">
              <div class="details-card">
                <h6>Employee Details</h6>
                <div class="details-row"><span>Name:</span><strong>{{ selectedPayslip.firstName }} {{ selectedPayslip.lastName }}</strong></div>
                <div class="details-row"><span>Employee No:</span>{{ selectedPayslip.empNo || 'N/A' }}</div>
                <div class="details-row"><span>Email:</span>{{ selectedPayslip.email || 'N/A' }}</div>
                <div class="details-row"><span>KRA PIN:</span>{{ selectedPayslip.kra_pin || selectedPayslip.kraPin || 'N/A' }}</div>
              </div>
              <div class="details-card">
                <h6>Bank Details</h6>
                <div class="details-row"><span>Bank:</span>{{ selectedPayslip.emp_bank_name || selectedPayslip.salary_bank_name || 'N/A' }}</div>
                <div class="details-row"><span>Account:</span>{{ selectedPayslip.emp_account_number || selectedPayslip.salary_account_number || 'N/A' }}</div>
              </div>
            </div>

            <div class="payslip-grid">
              <div class="earnings-card">
                <h6>Earnings</h6>
                <div class="earnings-row"><span>Basic Salary</span><span>{{ formatMoney(selectedPayslip.basic_salary) }}</span></div>
                <div class="earnings-row"><span>Allowances</span><span>{{ formatMoney(payslipAllowances(selectedPayslip)) }}</span></div>
                <div class="earnings-row total"><span>Gross Pay</span><span>{{ formatMoney(selectedPayslip.gross_pay) }}</span></div>
              </div>
              <div class="deductions-card">
                <h6>Deductions</h6>
                <div class="deductions-row"><span>PAYE</span><span>{{ formatMoney(selectedPayslip.paye) }}</span></div>
                <div class="deductions-row"><span>NSSF</span><span>{{ formatMoney(selectedPayslip.nssf) }}</span></div>
                <div class="deductions-row"><span>SHA/SHIF</span><span>{{ formatMoney(selectedPayslip.shif) }}</span></div>
                <div class="deductions-row"><span>Housing Levy</span><span>{{ formatMoney(selectedPayslip.housing_levy) }}</span></div>
                <div class="deductions-row"><span>Other Deductions</span><span>{{ formatMoney(selectedPayslip.other_deductions) }}</span></div>
                <div class="deductions-row total"><span>Total Deductions</span><span>{{ formatMoney(selectedPayslip.total_deductions) }}</span></div>
              </div>
            </div>

            <div class="payslip-net">
              <span>Net Pay</span>
              <strong>{{ formatMoney(selectedPayslip.net_pay) }}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from '@/api';
import NavigationBar from '@/components/Navigation.vue';

export default {
  name: 'PayrollHub',
  components: { NavigationBar },
  data() {
    const now = new Date();
    return {
      loading: false,
      message: '',
      payrollConfigs: [],
      runs: [],
      items: [],
      selectedConfigId: Number(this.$route.query.config_id || 0),
      runMonth: now.getMonth() + 1,
      runYear: now.getFullYear(),
      p9Year: now.getFullYear(),
      payrollMode: 'monthly',
      lastRunId: null,
      selectedPayslip: null,
      activeTab: 'runs',
      exportPreview: null,
      exportPreviewLimit: 12,
      companyInfo: {},
      registerSearch: ''
    };
  },
  computed: {
    exportPreviewTableRows() {
      if (!this.exportPreview?.rows) return [];
      return this.exportPreview.rows.slice(0, this.exportPreviewLimit);
    },
    selectedRun() {
      if (!this.lastRunId) return null;
      return this.runs.find((r) => Number(r.id) === Number(this.lastRunId)) || null;
    },
    selectedConfigName() {
      const cfg = this.payrollConfigs.find((c) => Number(c.id) === Number(this.selectedConfigId));
      return cfg?.config_name || `Config #${this.selectedConfigId || 'N/A'}`;
    },
    companyDisplayName() {
      return this.companyInfo.companyName || this.companyInfo.countyName || 'StaffSync';
    },
    companyLogoUrl() {
      const raw = this.companyInfo.logo || '';
      if (!raw) return '';
      if (/^https?:\/\//i.test(raw)) return raw;
      const base = String(authUrl || '').replace(/\/index\.php.*$/i, '');
      return `${base}/${String(raw).replace(/^\/+/, '')}`;
    },
    payslipPeriodText() {
      const r = this.selectedRun;
      if (!r) return `${this.runMonth}/${this.runYear}`;
      const m = Number(r.month || this.runMonth);
      const y = Number(r.year || this.runYear);
      const d = new Date(y, Math.max(0, m - 1), 1);
      return `${d.toLocaleString('en-KE', { month: 'long' })} ${y}`;
    },
    periodEndLabel() {
      const now = new Date();
      const rawYear = Number(this.runYear);
      const rawMonth = Number(this.runMonth);
      const y = Number.isFinite(rawYear) && rawYear >= 2000 ? rawYear : now.getFullYear();
      const m = Number.isFinite(rawMonth) && rawMonth >= 1 && rawMonth <= 12 ? rawMonth : (now.getMonth() + 1);
      const d = new Date(y, m, 0).getDate();
      return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    },
    itemsNetTotal() {
      return this.items.reduce((sum, it) => sum + (Number(it.net_pay) || 0), 0);
    },
    registerTotals() {
      return this.registerRows.reduce((acc, it) => {
        acc.gross += it.gross_pay;
        acc.deductions += it.total_deductions;
        acc.net += it.net_pay;
        return acc;
      }, { gross: 0, deductions: 0, net: 0 });
    },
    filteredRegisterRows() {
      const q = String(this.registerSearch || '').trim().toLowerCase();
      if (!q) return this.registerRows;
      return this.registerRows.filter((it) => {
        return [
          it.empNo,
          it.firstName,
          it.lastName,
          it.employee_name,
          it.kra_pin,
          it.bank_name,
          it.account_no
        ].some((v) => String(v || '').toLowerCase().includes(q));
      });
    },
    registerRows() {
      return this.items.map((it) => {
        const breakdown = this.safeParseJson(it.breakdown_json);
        const earnings = breakdown?.components?.earnings;
        const allowancesTotal = Array.isArray(earnings)
          ? earnings.reduce((s, e) => s + (Number(e.amount) || 0), 0)
          : Math.max(0, (Number(it.gross_pay) || 0) - (Number(it.basic_salary) || 0));
        const b = this.bankFields(it);
        return {
          id: it.id,
          empNo: it.empNo,
          firstName: it.firstName,
          lastName: it.lastName,
          employee_name: `${it.firstName || ''} ${it.lastName || ''}`.trim(),
          kra_pin: it.kra_pin || it.kraPin || '',
          bank_name: b.bank,
          account_no: b.account,
          basic_salary: Number(it.basic_salary) || 0,
          allowances_total: allowancesTotal,
          gross_pay: Number(it.gross_pay) || 0,
          other_deductions: Number(it.other_deductions) || 0,
          paye: Number(it.paye) || 0,
          nssf: Number(it.nssf) || 0,
          shif: Number(it.shif) || 0,
          housing_levy: Number(it.housing_levy) || 0,
          additional_statutory: Number(it.additional_statutory) || 0,
          total_deductions: Number(it.total_deductions) || 0,
          net_pay: Number(it.net_pay) || 0,
          raw: it
        };
      });
    }
  },
  watch: {
    activeTab(val) {
      if (val === 'master' && this.lastRunId) {
        this.loadItems();
      }
    },
    payrollMode() {
      this.lastRunId = null;
      this.items = [];
      this.loadRuns();
    },
    selectedConfigId() {
      this.lastRunId = null;
      this.items = [];
      this.loadRuns();
    }
  },
  mounted() {
    this.loadPayrollConfigs();
    this.loadRuns();
    this.loadCompanyInfo();
  },
  methods: {
    formatMoney(v) {
      return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 2 }).format(Number(v) || 0);
    },
    setMsg(t) {
      this.message = t;
      setTimeout(() => { if (this.message === t) this.message = ''; }, 3000);
    },
    async post(fn, extra = {}) {
      const data = new FormData();
      data.append('function', fn);
      Object.keys(extra).forEach((k) => {
        const v = extra[k];
        if (v !== undefined && v !== null && v !== '') data.append(k, v);
      });
      return execute(data, authUrl);
    },
    async loadPayrollConfigs() {
      try {
        const res = await this.post('getPayrollConfigs');
        if (res.data?.success) {
          const list = res.data.data?.payroll_configs || [];
          this.payrollConfigs = list;
          const defaultId = Number(res.data.data?.default_config_id || 0);
          const fromQuery = Number(this.$route.query.config_id || 0);
          if (fromQuery && list.find((c) => Number(c.id) === fromQuery)) {
            this.selectedConfigId = fromQuery;
          } else if (defaultId && list.find((c) => Number(c.id) === defaultId)) {
            this.selectedConfigId = defaultId;
          } else if (list.length && !list.find((c) => Number(c.id) === Number(this.selectedConfigId))) {
            this.selectedConfigId = Number(list[0].id);
          }
        }
      } catch (e) {}
    },
    csvEscape(v) {
      const s = String(v ?? '');
      return `"${s.replace(/"/g, '""')}"`;
    },
    safeParseJson(v) {
      if (!v || typeof v !== 'string') return null;
      try {
        return JSON.parse(v);
      } catch (e) {
        return null;
      }
    },
    bankFields(it) {
      const bank = it.emp_bank_name || it.bankName || it.bank_name || it.salary_bank_name || '';
      const account = it.emp_account_number || it.accountNumber || it.account_number || it.salary_account_number || '';
      const holder = it.emp_account_holder || it.accountHolder || it.account_holder || '';
      return { bank, account, holder };
    },
    exportStampSlug() {
      const r = this.selectedRun;
      if (r) return `${r.year}-${String(r.month).padStart(2, '0')}-run${r.id}`;
      return `run${this.lastRunId || 'x'}`;
    },
    openExportPreview({ title, fileName, headers, rows, excelUtf8 = false, successMsg }) {
      this.exportPreview = { title: title || 'Export', fileName, headers, rows: rows || [], excelUtf8: !!excelUtf8, successMsg: successMsg || 'File downloaded.' };
    },
    closeExportPreview() {
      this.exportPreview = null;
    },
    confirmExportDownload() {
      const p = this.exportPreview;
      if (!p) return;
      this.downloadCsv(p.fileName, p.headers, p.rows, p.excelUtf8);
      this.setMsg(p.successMsg);
      this.closeExportPreview();
    },
    downloadCsv(fileName, headers, rows, excelUtf8 = false) {
      const lines = [];
      lines.push(headers.map((h) => this.csvEscape(h)).join(','));
      rows.forEach((r) => lines.push(r.map((c) => this.csvEscape(c)).join(',')));
      let body = lines.join('\n');
      if (excelUtf8) body = `\uFEFF${body}`;
      const blob = new Blob([body], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
    openPayslip(item) {
      this.selectedPayslip = item;
    },
    payslipAllowances(item) {
      const breakdown = this.safeParseJson(item?.breakdown_json);
      const earnings = breakdown?.components?.earnings;
      if (Array.isArray(earnings)) {
        return earnings.reduce((s, e) => s + (Number(e.amount) || 0), 0);
      }
      return Math.max(0, (Number(item?.gross_pay) || 0) - (Number(item?.basic_salary) || 0));
    },
    payslipPayAfterTax(item) {
      const gross = Number(item?.gross_pay) || 0;
      const nssf = Number(item?.nssf) || 0;
      const shif = Number(item?.shif) || 0;
      const housing = Number(item?.housing_levy) || 0;
      const paye = Number(item?.paye) || 0;
      return gross - (nssf + shif + housing + paye);
    },
    async loadCompanyInfo() {
      try {
        const res = await this.post('getCompanyInfo');
        if (res.data?.success && res.data?.data?.company) {
          this.companyInfo = res.data.data.company;
        }
      } catch (e) {}
    },
    async sendPayslipEmail(item) {
      const toEmail = item?.email;
      if (!toEmail) return this.setMsg('Employee email is missing.');
      if (!this.lastRunId) return this.setMsg('Select payroll run first.');
      this.loading = true;
      try {
        const res = await this.post('sendPayrollRunPayslipEmail', { payroll_run_id: this.lastRunId, payroll_item_id: item.id });
        this.setMsg(res.data?.message || 'Payslip email sent.');
      } catch (e) {
        this.setMsg('Failed to send email.');
      } finally {
        this.loading = false;
      }
    },
    async sendAllPayslipsEmail() {
      if (!this.lastRunId) return this.setMsg('Select payroll run first.');
      if (!confirm('Send payslips to all employees?')) return;
      this.loading = true;
      try {
        const res = await this.post('sendPayrollRunPayslipsBulk', { payroll_run_id: this.lastRunId });
        this.setMsg(res.data?.message || 'Bulk email sent.');
      } catch (e) {
        this.setMsg('Failed to send bulk emails.');
      } finally {
        this.loading = false;
      }
    },
    async generateRun() {
      this.loading = true;
      try {
        const res = await this.post('generatePayroll', { payroll_config_id: this.selectedConfigId, month: this.runMonth, year: this.runYear, payroll_mode: this.payrollMode });
        if (res.data?.success) {
          this.lastRunId = Number(res.data.data?.payroll_run_id || 0) || this.lastRunId;
          this.setMsg(res.data.message || 'Generated');
          await this.loadRuns();
        } else this.setMsg(res.data?.message || 'Generate failed');
      } catch (e) {
        this.setMsg('Generate failed');
      } finally {
        this.loading = false;
      }
    },
    async loadRuns() {
      this.loading = true;
      try {
        const res = await this.post('getPayrollRuns', { payroll_config_id: this.selectedConfigId, payroll_mode: this.payrollMode });
        if (res.data?.success) {
          this.runs = res.data.data?.payroll_runs || [];
          if (!this.lastRunId && this.runs.length) this.lastRunId = Number(this.runs[0].id);
          this.setMsg(`Loaded ${this.runs.length} run(s).`);
        } else this.setMsg(res.data?.message || 'Failed');
      } catch (e) {
        this.setMsg('Failed to load runs');
      } finally {
        this.loading = false;
      }
    },
    async loadItems() {
      if (!this.lastRunId) return this.setMsg('Select a run first');
      this.loading = true;
      try {
        const res = await this.post('getPayrollItems', { payroll_run_id: this.lastRunId });
        if (res.data?.success) {
          this.items = res.data.data?.items || [];
          this.setMsg(`Loaded ${this.items.length} line(s).`);
        } else this.setMsg(res.data?.message || 'Failed');
      } catch (e) {
        this.setMsg('Failed to load lines');
      } finally {
        this.loading = false;
      }
    },
    async ensureItemsForExport() {
      if (!this.lastRunId) {
        if (!this.runs.length) await this.loadRuns();
        if (this.runs.length && !this.lastRunId) this.lastRunId = Number(this.runs[0].id);
      }
      if (!this.lastRunId) { this.setMsg('No payroll run found.'); return false; }
      if (!this.items.length) await this.loadItems();
      if (!this.items.length) { this.setMsg('No line items found.'); return false; }
      return true;
    },
    async approveRun() {
      if (!this.lastRunId) return this.setMsg('Set run ID');
      this.loading = true;
      try {
        const res = await this.post('approvePayrollRun', { payroll_run_id: this.lastRunId });
        this.setMsg(res.data?.message || 'Approved');
        if (res.data?.success) await this.loadRuns();
      } catch (e) {
        this.setMsg('Approve failed');
      } finally {
        this.loading = false;
      }
    },
    async markPaid() {
      if (!this.lastRunId) return this.setMsg('Set run ID');
      this.loading = true;
      try {
        const res = await this.post('markPayrollRunPaid', { payroll_run_id: this.lastRunId });
        const sync = res.data?.data?.history_sync;
        let msg = res.data?.message || 'Marked paid';
        if (sync && (sync.synced || sync.updated)) {
          msg += ` (${(sync.synced || 0) + (sync.updated || 0)} history records)`;
        }
        this.setMsg(msg);
        if (res.data?.success) await this.loadRuns();
      } catch (e) {
        this.setMsg('Mark paid failed');
      } finally {
        this.loading = false;
      }
    },
    async prepareBankPaymentExport() {
      if (!await this.ensureItemsForExport()) return;
      const stamp = this.exportStampSlug();
      const ref = `PAY-${stamp}`;
      const headers = ['Emp No', 'Beneficiary name', 'Bank name', 'Account number', 'Amount (KES)', 'Payment reference'];
      const rows = this.items.map((it) => {
        const b = this.bankFields(it);
        const name = (b.holder || `${it.firstName || ''} ${it.lastName || ''}`).trim();
        return [it.empNo || '', name, b.bank, b.account, (Number(it.net_pay) || 0).toFixed(2), ref];
      });
      this.openExportPreview({ title: 'Bank payment file', fileName: `bank_payment_${stamp}.csv`, headers, rows, excelUtf8: true, successMsg: 'Bank payment file downloaded.' });
    },
    async prepareMasterRegisterExport() {
      if (!await this.ensureItemsForExport()) return;
      const stamp = this.exportStampSlug();
      const headers = ['Emp No', 'First name', 'Last name', 'Bank', 'Account', 'Basic', 'Allowances', 'Gross', 'Other deductions', 'PAYE', 'NSSF', 'SHA/SHIF', 'Housing levy', 'Extra statutory', 'Total deductions', 'Net pay'];
      const rows = this.registerRows.map((it) => [
        it.empNo || '', it.firstName || '', it.lastName || '', it.bank_name || '', it.account_no || '',
        it.basic_salary.toFixed(2), it.allowances_total.toFixed(2), it.gross_pay.toFixed(2),
        it.other_deductions.toFixed(2), it.paye.toFixed(2), it.nssf.toFixed(2), it.shif.toFixed(2),
        it.housing_levy.toFixed(2), it.additional_statutory.toFixed(2), it.total_deductions.toFixed(2), it.net_pay.toFixed(2)
      ]);
      this.openExportPreview({ title: 'Master payroll register', fileName: `master_payroll_register_${stamp}.csv`, headers, rows, excelUtf8: true, successMsg: 'Master register downloaded.' });
    },
    async prepareComplianceExport(kind) {
      if (!await this.ensureItemsForExport()) return;
      const base = this.registerRows.map((it) => ({
        empNo: it.empNo || '',
        employee: it.employee_name || '',
        kraPin: it.kra_pin || '',
        nssfNumber: it.nssf_number || '',
        shifNumber: it.shif_number || '',
        gross: Number(it.gross_pay) || 0,
        taxable: Number(it.raw?.taxable_pay) || 0,
        paye: Number(it.paye) || 0,
        nssf: Number(it.nssf) || 0,
        shif: Number(it.shif) || 0,
        housing: Number(it.housing_levy) || 0
      }));
      const stamp = this.exportStampSlug();
      if (kind === 'KRA') {
        this.openExportPreview({ title: 'KRA (PAYE)', fileName: `payroll_kra_paye_${stamp}.csv`, headers: ['Emp No', 'Employee', 'KRA PIN', 'Taxable Pay', 'PAYE'], rows: base.map((r) => [r.empNo, r.employee, r.kraPin, r.taxable.toFixed(2), r.paye.toFixed(2)]) });
      } else if (kind === 'NSSF') {
        this.openExportPreview({ title: 'NSSF', fileName: `payroll_nssf_${stamp}.csv`, headers: ['Emp No', 'Employee', 'NSSF Number', 'Gross Pay', 'Employee NSSF'], rows: base.map((r) => [r.empNo, r.employee, r.nssfNumber, r.gross.toFixed(2), r.nssf.toFixed(2)]) });
      } else if (kind === 'SHA') {
        this.openExportPreview({ title: 'SHA/SHIF', fileName: `payroll_sha_shif_${stamp}.csv`, headers: ['Emp No', 'Employee', 'SHIF Number', 'Gross Pay', 'SHA/SHIF'], rows: base.map((r) => [r.empNo, r.employee, r.shifNumber, r.gross.toFixed(2), r.shif.toFixed(2)]) });
      } else if (kind === 'HOUSING') {
        this.openExportPreview({ title: 'Housing Levy', fileName: `payroll_housing_levy_${stamp}.csv`, headers: ['Emp No', 'Employee', 'Gross Pay', 'Housing Levy'], rows: base.map((r) => [r.empNo, r.employee, r.gross.toFixed(2), r.housing.toFixed(2)]) });
      }
    },
    async prepareP9Export() {
      const year = Number(this.p9Year);
      if (!year || year < 2000) return this.setMsg('Set a valid year');
      this.loading = true;
      try {
        const runsRes = await this.post('getPayrollRuns', { payroll_config_id: this.selectedConfigId, payroll_mode: this.payrollMode });
        if (!runsRes.data?.success) return this.setMsg('Failed to load runs');
        const allRuns = (runsRes.data.data?.payroll_runs || []).filter((r) => Number(r.year) === year);
        const agg = {};
        for (const r of allRuns) {
          const itemsRes = await this.post('getPayrollItems', { payroll_run_id: r.id });
          if (!itemsRes.data?.success) continue;
          (itemsRes.data.data?.items || []).forEach((it) => {
            const key = String(it.employee_id || it.id);
            if (!agg[key]) agg[key] = { empNo: it.empNo || '', employee: `${it.firstName || ''} ${it.lastName || ''}`.trim(), gross: 0, nssf: 0, housing: 0, taxable: 0, paye: 0 };
            agg[key].gross += Number(it.gross_pay) || 0;
            agg[key].nssf += Number(it.nssf) || 0;
            agg[key].housing += Number(it.housing_levy) || 0;
            agg[key].taxable += Number(it.taxable_pay) || 0;
            agg[key].paye += Number(it.paye) || 0;
          });
        }
        const list = Object.values(agg);
        const headers = ['Emp No', 'Employee', 'Annual Gross', 'Annual NSSF', 'Annual Housing', 'Annual Taxable', 'Annual PAYE'];
        const rows = list.map((r) => [r.empNo, r.employee, r.gross.toFixed(2), r.nssf.toFixed(2), r.housing.toFixed(2), r.taxable.toFixed(2), r.paye.toFixed(2)]);
        this.openExportPreview({ title: `P9 summary — ${year}`, fileName: `payroll_p9_${year}.csv`, headers, rows, excelUtf8: true, successMsg: `P9 export for ${year} downloaded.` });
      } catch (e) {
        this.setMsg('Failed to export P9');
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
  /* Layout */
  .content-minimize {
    margin-left: 250px;
    padding: 20px 24px;
    background: linear-gradient(135deg, #f5f7fc 0%, #eef2f8 100%);
    min-height: 100vh;
  }

  @media (max-width: 992px) {
    .content-minimize {
      margin-left: 0;
      padding: 16px;
    }
  }

  /* Header */
  .payroll-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 24px;
  }

  .payroll-header-left {
    flex: 1;
  }

  .payroll-badge {
    display: inline-block;
    background: linear-gradient(135deg, #4f46e5, #6366f1);
    color: white;
    font-size: 11px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 20px;
    letter-spacing: 0.5px;
    margin-bottom: 12px;
  }

  .payroll-title {
    font-size: 28px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 6px 0;
    letter-spacing: -0.02em;
  }

  .payroll-description {
    font-size: 14px;
    color: #64748b;
    margin: 0;
  }

  .btn-config {
    background: white;
    border: 1px solid #e2e8f0;
    padding: 10px 20px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 500;
    color: #475569;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s;
  }

  .btn-config:hover {
    border-color: #4f46e5;
    color: #4f46e5;
    background: #f8fafc;
  }

  /* Stat Cards */
  .stat-card {
    background: white;
    border-radius: 20px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    border: 1px solid #eef2ff;
    transition: all 0.2s;
  }

  .stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px -12px rgba(0, 0, 0, 0.1);
  }

  .stat-card-icon {
    width: 52px;
    height: 52px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    color: white;
  }

  .stat-card-icon.blue { background: linear-gradient(135deg, #3b82f6, #2563eb); }
  .stat-card-icon.purple { background: linear-gradient(135deg, #8b5cf6, #7c3aed); }
  .stat-card-icon.green { background: linear-gradient(135deg, #10b981, #059669); }
  .stat-card-icon.orange { background: linear-gradient(135deg, #f59e0b, #d97706); }

  .stat-card-info {
    flex: 1;
  }

  .stat-card-value {
    font-size: 28px;
    font-weight: 700;
    color: #0f172a;
    display: block;
    line-height: 1.2;
  }

  .stat-card-label {
    font-size: 13px;
    color: #64748b;
  }

  /* Main Card */
  .payroll-card {
    background: white;
    border-radius: 24px;
    border: 1px solid #eef2ff;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
  }

  .payroll-card-header {
    padding: 20px 24px 0 24px;
  }

  .payroll-card-title {
    font-size: 18px;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
  }

  .payroll-card-body {
    padding: 20px 24px 24px 24px;
  }

  /* Filters */
  .filters-row {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 20px;
  }

  .filter-group {
    flex: 1;
    min-width: 120px;
  }

  .filter-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .filter-select, .filter-input {
    width: 100%;
    padding: 10px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    font-size: 14px;
    color: #1e293b;
    background: white;
    transition: all 0.2s;
  }

  .filter-select:focus, .filter-input:focus {
    outline: none;
    border-color: #4f46e5;
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
  }

  /* Info Badge */
  .info-badge {
    background: #f8fafc;
    border-radius: 12px;
    padding: 12px 16px;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #475569;
    border: 1px solid #eef2ff;
  }

  .info-badge i {
    color: #4f46e5;
    font-size: 18px;
  }

  /* Tabs */
  .payroll-tabs {
    display: flex;
    gap: 8px;
    border-bottom: 1px solid #eef2ff;
    margin-bottom: 20px;
  }

  .tab-btn {
    background: none;
    border: none;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    color: #64748b;
    cursor: pointer;
    border-radius: 12px 12px 0 0;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .tab-btn i {
    font-size: 18px;
  }

  .tab-btn:hover {
    color: #4f46e5;
    background: #f8fafc;
  }

  .tab-btn.active {
    color: #4f46e5;
    background: white;
    border-bottom: 2px solid #4f46e5;
  }

  /* Message Alert */
  .message-alert {
    background: #f1f5f9;
    border-radius: 12px;
    padding: 12px 16px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #475569;
  }

  /* Action Buttons */
  .action-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 20px;
  }

  .btn-primary-sm, .btn-outline-sm, .btn-success-sm, .btn-dark-sm, .btn-email-sm {
    padding: 8px 18px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .btn-primary-sm {
    background: #4f46e5;
    border: none;
    color: white;
  }

  .btn-primary-sm:hover {
    background: #6366f1;
    transform: translateY(-1px);
  }

  .btn-outline-sm {
    background: transparent;
    border: 1px solid #cbd5e1;
    color: #475569;
  }

  .btn-outline-sm:hover {
    border-color: #4f46e5;
    color: #4f46e5;
    background: #f8fafc;
  }

  .btn-success-sm {
    background: #10b981;
    border: none;
    color: white;
  }

  .btn-success-sm:hover {
    background: #059669;
  }

  .btn-dark-sm {
    background: #1e293b;
    border: none;
    color: white;
  }

  .btn-dark-sm:hover {
    background: #0f172a;
  }

  .btn-email-sm {
    background: #f59e0b;
    border: none;
    color: white;
  }

  .btn-email-sm:hover {
    background: #d97706;
  }

  /* Compliance Section */
  .compliance-section {
    background: #f8fafc;
    border-radius: 16px;
    padding: 16px;
    margin-bottom: 24px;
  }

  .compliance-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
  }

  .compliance-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
  }

  .compliance-btn {
    background: white;
    border: 1px solid #e2e8f0;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    color: #475569;
    cursor: pointer;
    transition: all 0.2s;
  }

  .compliance-btn:hover {
    border-color: #4f46e5;
    color: #4f46e5;
  }

  .compliance-btn.green {
    background: #10b981;
    border-color: #10b981;
    color: white;
  }

  .compliance-btn.green:hover {
    background: #059669;
  }

  .p9-group {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .p9-input {
    width: 80px;
    padding: 6px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    font-size: 12px;
  }

  /* Section Title */
  .section-title-sm {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
  }

  /* Tables */
  .table-container {
    border-radius: 16px;
    border: 1px solid #eef2ff;
    overflow-x: auto;
    background: white;
  }

  .data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }

  .data-table th {
    text-align: left;
    padding: 14px 16px;
    background: #f8fafc;
    color: #475569;
    font-weight: 600;
    border-bottom: 1px solid #eef2ff;
  }

  .data-table td {
    padding: 12px 16px;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
  }

  .data-table tr:hover {
    background: #faf5ff;
    cursor: pointer;
  }

  .data-table tr.selected {
    background: #eef2ff;
  }

  .id-cell {
    font-weight: 600;
    color: #4f46e5;
  }

  .amount-cell {
    font-weight: 500;
  }

  .amount-cell.net {
    font-weight: 700;
    color: #10b981;
  }

  .status-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
  }

  .status-badge.draft { background: #fef3c7; color: #d97706; }
  .status-badge.approved { background: #d1fae5; color: #059669; }
  .status-badge.paid { background: #dbeafe; color: #2563eb; }

  /* Employee Cell */
  .employee-cell {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .employee-avatar {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #4f46e5, #818cf8);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 12px;
    font-weight: 600;
  }

  .employee-info {
    display: flex;
    flex-direction: column;
  }

  .employee-name {
    font-weight: 600;
    color: #1e293b;
  }

  .employee-id {
    font-size: 11px;
    color: #94a3b8;
  }

  /* Action Icons */
  .action-cell {
    display: flex;
    gap: 8px;
  }

  .icon-btn {
    background: transparent;
    border: none;
    font-size: 18px;
    cursor: pointer;
    padding: 6px;
    border-radius: 8px;
    transition: all 0.2s;
    color: #94a3b8;
  }

  .icon-btn:hover {
    background: #f1f5f9;
    color: #4f46e5;
  }

  .icon-btn.email:hover {
    color: #f59e0b;
  }

  /* Stats Mini */
  .stats-mini-row {
    display: flex;
    gap: 24px;
    margin-bottom: 20px;
    flex-wrap: wrap;
  }

  .stat-mini {
    background: #f8fafc;
    padding: 12px 20px;
    border-radius: 12px;
    text-align: center;
  }

  .stat-mini-label {
    display: block;
    font-size: 11px;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .stat-mini-value {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
  }

  /* Search Row */
  .search-row {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #f8fafc;
    border-radius: 12px;
    padding: 8px 14px;
    margin-bottom: 20px;
    border: 1px solid #eef2ff;
  }

  .search-row i {
    color: #94a3b8;
    font-size: 18px;
  }

  .search-input {
    flex: 1;
    border: none;
    background: transparent;
    outline: none;
    font-size: 13px;
  }

  /* Empty State */
  .empty-state {
    text-align: center;
    padding: 48px 24px;
    color: #94a3b8;
  }

  .empty-state i {
    font-size: 48px;
    margin-bottom: 16px;
    display: block;
  }

  .empty-state p {
    margin: 0;
    font-size: 14px;
  }

  /* Warning Badge */
  .warning-badge {
    background: #fef3c7;
    border-radius: 12px;
    padding: 12px 16px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #d97706;
  }

  .run-info {
    background: #dbeafe;
    border-radius: 12px;
    padding: 12px 16px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #1e40af;
  }

  /* Modals */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal-container {
    background: white;
    border-radius: 24px;
    width: min(960px, 90vw);
    max-height: 85vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #eef2ff;
  }

  .modal-header h5 {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
  }

  .modal-close {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #94a3b8;
  }

  .modal-close:hover {
    color: #1e293b;
  }

  .modal-body {
    padding: 24px;
    overflow-y: auto;
    flex: 1;
  }

  .modal-footer {
    padding: 16px 24px;
    border-top: 1px solid #eef2ff;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }

  /* Preview Table */
  .preview-table-wrapper {
    overflow-x: auto;
    border-radius: 12px;
    border: 1px solid #eef2ff;
  }

  .preview-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }

  .preview-table th, .preview-table td {
    padding: 10px 12px;
    border-bottom: 1px solid #f1f5f9;
    text-align: left;
  }

  .preview-table th {
    background: #f8fafc;
    font-weight: 600;
    color: #475569;
  }

  /* Payslip */
  .payslip {
    background: white;
  }

  .payslip-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-bottom: 20px;
    border-bottom: 1px solid #eef2ff;
    margin-bottom: 20px;
  }

  .payslip-company {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .payslip-logo {
    width: 56px;
    height: 56px;
    object-fit: contain;
    border-radius: 12px;
    border: 1px solid #eef2ff;
  }

  .payslip-company h4 {
    font-size: 18px;
    font-weight: 700;
    margin: 0 0 4px 0;
  }

  .company-address {
    font-size: 12px;
    color: #64748b;
    margin: 0;
  }

  .payslip-meta {
    text-align: right;
  }

  .payslip-badge {
    display: inline-block;
    background: #4f46e5;
    color: white;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  .payslip-period {
    display: block;
    font-size: 12px;
    color: #64748b;
  }

  .payslip-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 24px;
  }

  .details-card {
    background: #f8fafc;
    border-radius: 16px;
    padding: 16px;
  }

  .details-card h6 {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    margin: 0 0 12px 0;
  }

  .details-row {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    padding: 6px 0;
    color: #64748b;
  }

  .details-row span:first-child {
    color: #94a3b8;
  }

  .payslip-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 24px;
  }

  .earnings-card, .deductions-card {
    border: 1px solid #eef2ff;
    border-radius: 16px;
    padding: 16px;
  }

  .earnings-card h6, .deductions-card h6 {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    margin: 0 0 12px 0;
  }

  .earnings-row, .deductions-row {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    padding: 8px 0;
    border-bottom: 1px solid #f1f5f9;
  }

  .earnings-row.total, .deductions-row.total {
    border-bottom: none;
    font-weight: 700;
    color: #1e293b;
    padding-top: 12px;
    margin-top: 4px;
  }

  .payslip-net {
    background: linear-gradient(135deg, #4f46e5, #6366f1);
    border-radius: 16px;
    padding: 16px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: white;
  }

  .payslip-net span {
    font-size: 14px;
    opacity: 0.9;
  }

  .payslip-net strong {
    font-size: 24px;
    font-weight: 700;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .payroll-card-body {
      padding: 16px;
    }
    
    .filters-row {
      flex-direction: column;
    }
    
    .filter-group {
      width: 100%;
    }
    
    .payslip-details, .payslip-grid {
      grid-template-columns: 1fr;
    }
    
    .payroll-header {
      flex-direction: column;
      align-items: flex-start;
    }
    
    .stats-mini-row {
      flex-direction: column;
      gap: 12px;
    }
  }
</style>  